import { Button, CircularProgress, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Empty, Flex, Loader } from "@zfp-akademie-a-s/zfp-design";
import { ErrorIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import {
  type CreateAnalysisInvestmentCaseMutationError,
  createAnalysisInvestmentCase,
  getGetAnalysisQueryKey,
  useGetAnalysis,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { CreateInvestmentCaseNotFoundErrorCode } from "Infrastructure/ApiZfp/Schema";
import { ANALYZER_EMAIL } from "Infrastructure/Config/constants";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type PropsWithChildren, useContext } from "react";
import { AnalysisDetailContext } from "./AnalysisDetailContext";

/**
 * The BE creates the nSure investment case lazily from several endpoints (goals, questionnaire, portfolios) and
 * rejects a second creation running for the same analysis at the same time. Creating the case up front, before any
 * of those endpoints can be hit in parallel, keeps them from racing each other.
 */
export const InvestmentCaseGate = ({ children }: PropsWithChildren) => {
  const { t } = useResource();
  const { analysisId, versionId, readOnly } = useContext(AnalysisDetailContext);
  const queryClient = useQueryClient();

  const analysisQuery = useGetAnalysis(analysisId, versionId);
  const analysis = analysisQuery.data;
  const hasCase = !!analysis?.nsureCaseId;
  const isReadOnly = readOnly || !!analysis?.versions.find((version) => version.versionId === versionId)?.readOnly;

  // A query rather than a mutation: an in-flight creation is shared across StrictMode double mounts and remounts
  // instead of being sent twice. The abort signal is deliberately not passed - aborting would not stop the creation
  // on the BE, and the request fired by the next mount would then collide with it.
  const ensureCaseQuery = useQuery<boolean, CreateAnalysisInvestmentCaseMutationError>({
    queryKey: ["ensureAnalysisInvestmentCase", analysisId, versionId],
    queryFn: async () => {
      await createAnalysisInvestmentCase(analysisId, versionId);
      await queryClient.invalidateQueries({ queryKey: getGetAnalysisQueryKey(analysisId, versionId) });
      return true;
    },
    enabled: !!analysis && !hasCase && !isReadOnly,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    meta: { suppressErrorToast: true },
  });

  if (hasCase || isReadOnly || ensureCaseQuery.isSuccess) return <>{children}</>;

  const isFetching = analysisQuery.isFetching || ensureCaseQuery.isFetching;

  if (analysisQuery.isError && !isFetching) {
    return (
      <Empty
        title={t(Resources.AnalysisDetail.InvestmentCase.LoadError.Title)}
        text={t(Resources.AnalysisDetail.InvestmentCase.LoadError.Text, { email: ANALYZER_EMAIL })}
        iconSize={40}
        icon={<ErrorIcon sx={{ color: "grey.400" }} />}
        actions={
          <Button variant="contained" onClick={() => analysisQuery.refetch()}>
            {t(Resources.AnalysisDetail.InvestmentCase.Retry)}
          </Button>
        }
      />
    );
  }

  if (ensureCaseQuery.isError && !isFetching) {
    const isAgentMissing = ensureCaseQuery.error.code === CreateInvestmentCaseNotFoundErrorCode.nsure_agent_not_found;

    return (
      <Empty
        title={t(Resources.AnalysisDetail.InvestmentCase.CreateError.Title)}
        text={
          isAgentMissing
            ? t(Resources.AnalysisDetail.InvestmentCase.CreateError.AgentNotFoundText, { email: ANALYZER_EMAIL })
            : t(Resources.AnalysisDetail.InvestmentCase.CreateError.Text, { email: ANALYZER_EMAIL })
        }
        iconSize={40}
        icon={<ErrorIcon sx={{ color: "grey.400" }} />}
        actions={
          <Button variant="contained" onClick={() => ensureCaseQuery.refetch()}>
            {t(Resources.AnalysisDetail.InvestmentCase.Retry)}
          </Button>
        }
      />
    );
  }

  if (!analysis) return <Loader />;

  return (
    <Flex flexDirection="column" justifyContent="center" alignItems="center" minHeight="20vh" gap={Spacing.L}>
      <CircularProgress color="primary" />
      <Typography variant="body2" color="text.secondary">
        {t(Resources.AnalysisDetail.InvestmentCase.Creating)}
      </Typography>
    </Flex>
  );
};
